const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;

export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const normalizedStatus = status => status?.trim().toLowerCase();

function capitalize(str) {
  return str
    ? str
        .toString()
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase())
    : '';
}

export function formatSlabTitle(publicData, title, currentUser) {
  const { wood_species, wood_species_other, slab_number, log_number } = publicData;

  const species =
    wood_species && wood_species.toLowerCase() === 'other' ? wood_species_other : wood_species;

  const slabNum = slab_number ? ` ${slab_number}` : '';
  const logNum = log_number ? `-${log_number}` : '';
  const sellerName = currentUser?.attributes?.profile?.displayName || 'Seller';

  const reformedTitle = `${capitalize(species)} ${title} -${slabNum} ${logNum} from ${capitalize(
    sellerName
  )}`;

  return reformedTitle.trim();
}

export const getReformedTitle = l => {
  return l?.attributes?.publicData?.reformedTitle || l?.attributes?.title;
};

export function retrieveUserFavorites(user) {
  if (!user || !user.id) {
    return null;
  }
  const protectedData = user.attributes?.profile?.protectedData || {};
  return protectedData.favorites ?? [];
}

export const isWindowDefined = () => {
  return typeof window !== 'undefined';
};

export const cardRenderSizes = isMapVariant => {
  if (isMapVariant) {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) ${panelMediumWidth}vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  } else {
    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      `(max-width: 1439px) 26vw`,
      `(max-width: 1920px) 18vw`,
      `14vw`,
    ].join(', ');
  }
};

/**
 * Extracts a map of enum options from a Sharetribe field config.
 * Example: { 'half_inch': '1/2"', 'three_quarter': '3/4"' }
 *
 * @param {Object} fieldConfig - Field config with `enumOptions`
 * @returns {Record<string, string>} - Map of option keys to labels
 */
export function extractEnumMapFromFieldConfig(fieldConfig) {
  if (!fieldConfig?.enumOptions || !Array.isArray(fieldConfig.enumOptions)) return {};

  return fieldConfig.enumOptions.reduce((acc, curr) => {
    acc[curr.option] = curr.label;
    return acc;
  }, {});
}

/**
 * Returns the human-readable label for a given enum key using a provided map.
 *
 * @param {Record<string, string>} enumMap - Map of { option: label }
 * @param {string} key - The value saved in listing (e.g., 'three_quarter')
 * @returns {string} - Display label like '3/4"' or fallback value
 */
export function getLabelForEnumKey(enumMap, key) {
  return enumMap?.[key] || key || '—';
}

export const decryptNumber = encryptedNumber => {
  if (!encryptedNumber || !encryptionKey) return null;
  const decrypted = encryptedNumber ^ encryptionKey;
  return decrypted;
};

export const calculateDiscountedPrice = (price, couponForPlan, couponError) => {
  let discountedPrice = price;
  if (couponForPlan?.couponCode && !couponError) {
    const originalPrice = parseFloat(price.replace('$', '')) || 0;
    if (couponForPlan.type === 'percentage') {
      const discount = (originalPrice * couponForPlan?.discount) / 100;
      discountedPrice = `$${Math.max(0, originalPrice - discount).toFixed(2)}`;
    } else {
      const discountInDollars = couponForPlan.discount / 100;
      discountedPrice = `$${Math.max(0, originalPrice - discountInDollars).toFixed(2)}`;
    }
  }
  return discountedPrice;
};

export function removePubPrefix(obj) {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const newKey = key.startsWith('pub_') ? key.slice(4) : key;
    acc[newKey] = value;
    return acc;
  }, {});
}

export const isProfileDetailsExists = currentUser => {
  const publicData = currentUser?.attributes?.profile?.publicData || {};

  const hasKiln = !!publicData.kiln_services;
  const hasMilling = !!publicData.milling_services;
  const hasProduct = Array.isArray(publicData.product) && publicData.product.length > 0;

  return hasKiln && hasMilling && hasProduct;
};

export const getParentListings = listingsOrBatches => {
  const input = Array.isArray(listingsOrBatches) ? listingsOrBatches : [];

  const merged =
    typeof input.flat === 'function'
      ? input.flat(Infinity)
      : input.reduce((acc, x) => acc.concat(x), []);

  const seen = new Set();
  return merged.filter(l => {
    const id = l?.id?.uuid || l?.id;
    if (id && seen.has(id)) return false;
    if (id) seen.add(id);

    const pd = l?.attributes?.publicData || {};
    const lt = (pd.listingType ?? '')
      .toString()
      .trim()
      .toLowerCase();
    const ty = (pd.type ?? '')
      .toString()
      .trim()
      .toLowerCase();
    return ty === 'parent-listing' || lt === 'parent-listing';
  });
};

export const filterListingsAndParents = (listings = [], parentListings = []) => {
  const nonParentListings = (listings ?? []).filter(
    l => l?.attributes?.publicData?.type !== 'parent-listing'
  );

  const seen = new Set();
  const cleanedParents = (parentListings ?? []).filter(p => {
    const key = p?.author?.id?.uuid || p?.id?.uuid;
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { filteredListings: nonParentListings, filteredparentListings: cleanedParents };
};

export const getFilteredListings = listings => {
  return (listings || []).filter(
    listing => listing?.attributes?.publicData?.type != 'parent-listing'
  );
};
